import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  LayoutGrid,
  Minus,
  Percent,
  Plus,
  ScanBarcode,
  Search,
  ShoppingCart,
  Trash2,
  Wallet,
  X,
} from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Page } from '@/lib/paging'
import type { Product } from '@/lib/types'
import { formatLbp, formatQuantity, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState } from '@/components/ui/primitives'
import { useScanner } from '@/hooks/useScanner'
import { cartPreview, lineGross, useCart, type CartLine } from '@/stores/cart'
import { WeighDialog, abbreviate, trim } from '@/pages/checkout/WeighDialog'
import { CustomItemDialog, DiscountDialog } from '@/pages/checkout/CartDialogs'
import { useSession } from '@/stores/session'
import { t, tServer } from '@/i18n'
import { Permission } from '@/lib/permissions'
import { OpenShiftGate } from '@/pages/checkout/OpenShiftGate'
import { AttachedCustomerRow, CustomerPicker } from '@/pages/checkout/CustomerPicker'
import { PaymentSheet, type Tender } from '@/pages/checkout/PaymentSheet'
import { ReceiptDialog } from '@/pages/checkout/ReceiptDialog'

interface CheckoutSettings {
  vatMode: 'Inclusive' | 'Exclusive'
  vatEnabled: boolean
  lbpRoundingStep: number
  showLbpOnScreen: boolean
}

interface Register {
  id: number
  name: string
  branchId: number
  currentShiftId: number | null
}

export function CheckoutPage() {
  const queryClient = useQueryClient()
  const cart = useCart()
  const can = useSession((state) => state.can)
  const canDiscount = can(Permission.Discount)
  // Naming a price for something not in the catalogue is the same power as overriding a shelf
  // price, and the server holds it to the same right — so the button follows.

  const [search, setSearch] = useState('')
  /** The category being browsed. Null shows the categories themselves. */
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [isCustomOpen, setCustomOpen] = useState(false)
  /** The cart line having a discount set, if any. */
  const [discounting, setDiscounting] = useState<CartLine | null>(null)
  const [isOrderDiscountOpen, setOrderDiscountOpen] = useState(false)
  const [registerId, setRegisterId] = useState<number | null>(null)
  const [isPaymentOpen, setPaymentOpen] = useState(false)
  const [isCartOpen, setCartOpen] = useState(false)
  const [isCustomerOpen, setCustomerOpen] = useState(false)
  /** The scale item waiting to be weighed before it can join the cart. */
  const [weighing, setWeighing] = useState<Product | null>(null)
  const [completedSaleId, setCompletedSaleId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => apiClient.get<Register[]>('/api/registers'),
  })

  const settings = useQuery({
    queryKey: ['checkout', 'settings'],
    queryFn: () => apiClient.get<CheckoutSettings>('/api/sales/settings'),
  })

  const rate = useQuery({
    queryKey: ['exchange-rate'],
    queryFn: () => apiClient.get<{ lbpPerUsd: number }>('/api/exchange-rate'),
  })


  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.get<{ id: number; name: string }[]>('/api/categories'),
  })

  // Browsing by category, or searching across all of them. A search is answered from the whole
  // catalogue rather than the category in hand: someone typing a name wants the item, not a
  // reminder that they are standing in the wrong aisle.
  const browsingCategories = categoryId === null && search.trim() === ''
  const products = useQuery({
    queryKey: ['products', search, categoryId],
    queryFn: () => {
      // The full aisle, not the first page of it: browsing a category is how a cashier finds an
      // item with no barcode, and a page break in the middle of the vegetables is a sale lost.
      const params = new URLSearchParams({ search, pageSize: '200' })
      // A search looks across the whole catalogue rather than the aisle in hand: someone typing
      // a name wants the item, not a reminder that they are standing in the wrong section.
      if (categoryId !== null && search.trim() === '') params.set('categoryId', String(categoryId))
      // Only ever the first page here: a till search is answered by typing more, not by
      // scrolling. The browse-by-aisle path is capped the same way and for the same reason.
      return apiClient
        .get<Page<Product>>(`/api/products?${params}`)
        .then((page) => page.items)
    },
    enabled: !browsingCategories,
  })

  const shift = useQuery({
    queryKey: ['shift', registerId],
    queryFn: () => apiClient.get<{ id: number } | null>(`/api/shifts/current?registerId=${registerId}`),
    enabled: registerId !== null,
  })

  useEffect(() => {
    if (registerId === null && registers.data?.length) {
      setRegisterId(registers.data[0]!.id)
    }
  }, [registers.data, registerId])

  /** Resolves a scanned code and drops it straight into the cart. */
  const scan = useMutation({
    mutationFn: (code: string) => apiClient.get<Product>(`/api/products/barcode/${encodeURIComponent(code)}`),
    onSuccess: (product) => {
      // A scale item cannot be added by scanning alone — the barcode says what it is, not how
      // much of it, so the weight has to be asked for before it can be priced.
      if (product.sellsByWeight) {
        setWeighing(product)
        return
      }

      cart.add(product)
      setFlash(`${product.name} added`)
      setTimeout(() => setFlash(null), 1200)
    },
    onError: () => {
      setFlash(t('Unknown barcode'))
      setTimeout(() => setFlash(null), 1600)
    },
  })

  // Active whenever no dialog is up, so a scan works from anywhere on the screen — but never
  // while one is, or a scan would drop into the cart behind an open sheet unseen.
  useScanner((code) => scan.mutate(code), !isPaymentOpen && !isCustomerOpen)

  const vatInclusive = settings.data?.vatMode !== 'Exclusive'
  // With VAT off the preview must not add tax on top, or the total on screen would not match
  // what the server charges.
  const vatEnabled = settings.data?.vatEnabled !== false
  const preview = useMemo(
    () => cartPreview(cart.lines, cart.saleDiscountUsd, vatInclusive, vatEnabled),
    [cart.lines, cart.saleDiscountUsd, vatInclusive, vatEnabled],
  )

  const checkout = useMutation({
    mutationFn: async ({ tenders, changeInLbp }: { tenders: Tender[]; changeInLbp: boolean }) => {
      return apiClient.post<{ id: number; number: string }>('/api/sales', {
        // Generated per attempt and reused on retry, so a dropped response cannot charge twice.
        clientReference: crypto.randomUUID(),
        registerId,
        branchId: null,
        customerId: cart.customer?.id ?? null,
        lines: cart.lines.map((line) => ({
          // A one-off carries a negative synthetic id here; the server ignores it and uses its
          // own placeholder product, taking the name, price and cost from `custom` instead.
          productId: line.custom ? 0 : line.productId,
          // The weight as entered, with the unit it was entered in. The server converts it to
          // the product's stock unit — that arithmetic decides both the price and how much
          // stock leaves, so it is not settled here.
          quantity: line.quantity,
          quantityUnit: line.quantityUnit ?? null,
          unitPriceUsd: line.unitPriceUsd === line.listPriceUsd ? null : line.unitPriceUsd,
          discountUsd: line.discountUsd,
          custom: line.custom ?? null,
        })),
        saleDiscountUsd: cart.saleDiscountUsd,
        tenders,
        changeInLbp,
        note: cart.note || null,
      })
    },
    onSuccess: async (sale) => {
      setPaymentOpen(false)
      setCartOpen(false)
      setError(null)
      cart.clear()
      setCompletedSaleId(sale.id)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      // A sale put on account moves a balance, so the customer screens are stale now.
      await queryClient.invalidateQueries({ queryKey: ['customers'] })
      await queryClient.invalidateQueries({ queryKey: ['receivables'] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? tServer(caught.detail ?? caught.message)
          : t('Could not complete the sale.'),
      ),
  })

  if (registers.data?.length === 0) {
    return (
      <EmptyState
        icon={Wallet}
        title={t('No register available')}
        description={t('An administrator needs to add a register to this branch before you can sell.')}
      />
    )
  }

  if (registerId !== null && shift.isFetched && !shift.data) {
    return (
      <OpenShiftGate
        registerId={registerId}
        registerName={registers.data?.find((r) => r.id === registerId)?.name ?? 'this register'}
        onOpened={() => queryClient.invalidateQueries({ queryKey: ['shift'] })}
      />
    )
  }

  const lbpRate = rate.data?.lbpPerUsd ?? 0
  const step = settings.data?.lbpRoundingStep ?? 5000

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] flex-col lg:flex-row">
      {/* Products */}
      <section className="flex min-h-0 flex-1 flex-col p-3 sm:p-4">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('Scan a barcode or search by name')}
          leading={<Search className="h-4 w-4" />}
          autoComplete="off"
          wrapperClassName="mb-3"
        />

        {/* Where you are, and the way back. A grid of products with no way out of the category
            leaves a cashier reaching for the browser's back button mid-sale. */}
        <div className="mb-3 flex items-center gap-2">
          {!browsingCategories && search.trim() === '' && (
            <Button variant="secondary" size="sm" onClick={() => setCategoryId(null)}>
              <ChevronLeft className="h-4 w-4" />
              {t('Categories')}
            </Button>
          )}
          {categoryId !== null && search.trim() === '' && (
            <span className="truncate text-sm font-medium">
              {categories.data?.find((entry) => entry.id === categoryId)?.name}
            </span>
          )}
          {/* Open to anyone who can work the till. Ringing up the thing in front of you that
              the shop has never stocked is selling, not overriding a price — see SaleService. */}
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setCustomOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('Custom item')}
          </Button>
        </div>

        {flash && (
          <div
            role="status"
            className="surface-raised mb-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
          >
            <ScanBarcode className="h-4 w-4 text-[var(--accent)]" />
            {flash}
          </div>
        )}

        {/* The fixed cart bar sits over the bottom of this list on a phone, so the last row of
            products needs room to clear it. Desktop has no bar and needs no allowance. */}
        {/* pr-2 keeps the tiles off the scrollbar, which otherwise sits hard against the right
            edge of the grid and clips the hover border. */}
        <div
          className={`min-h-0 flex-1 overflow-y-auto pr-2 ${
            cart.lines.length > 0 ? 'pb-cart-bar lg:pb-0' : ''
          }`}
        >
          {browsingCategories ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
              {categories.data?.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setCategoryId(category.id)}
                  className="surface-raised flex touch-target min-h-24 flex-col justify-between rounded-[var(--radius-card)] border p-3 text-left transition-colors hover:border-[var(--accent)]"
                >
                  <LayoutGrid className="h-5 w-5 text-[var(--accent)]" />
                  <span className="mt-2 line-clamp-2 text-sm font-medium">{category.name}</span>
                </button>
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
            {products.data?.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => (product.sellsByWeight ? setWeighing(product) : cart.add(product))}
                disabled={!product.isActive}
                className="surface-raised flex touch-target flex-col justify-between rounded-[var(--radius-card)] border p-3 text-left transition-colors hover:border-[var(--accent)] disabled:opacity-40"
              >
                <span className="line-clamp-2 text-sm font-medium">{product.name}</span>
                <span className="mt-2 flex items-baseline justify-between gap-2">
                  {/* Priced per unit when sold by weight, so the grid says so rather than
                      looking like the price of one item. */}
                  <span className="tabular font-semibold">
                    {formatUsd(product.priceUsd)}
                    {product.sellsByWeight && (
                      <span className="text-xs font-normal text-[var(--text-secondary)]">
                        /{abbreviate(product.unit)}
                      </span>
                    )}
                  </span>
                  <span
                    className={
                      product.quantityOnHand <= 0
                        ? 'tabular text-xs text-danger-500'
                        : 'tabular text-xs text-[var(--text-secondary)]'
                    }
                  >
                    {formatQuantity(product.quantityOnHand)}
                  </span>
                </span>
              </button>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Cart — a side panel on desktop, a sheet on a phone */}
      <aside
        className={`surface-raised flex min-h-0 flex-col border-t lg:w-96 lg:border-l lg:border-t-0 ${
          isCartOpen ? 'fixed inset-x-0 bottom-0 top-14 z-40' : 'hidden lg:flex'
        }`}
      >
        <header className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3">
          <h2 className="font-semibold">{t('Cart')}</h2>
          <div className="flex items-center gap-2">
            {cart.lines.length > 0 && (
              <Button variant="ghost" size="sm" onClick={cart.clear}>
                {t('Clear')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label={t('Close cart')}
              onClick={() => setCartOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <AttachedCustomerRow
          customer={cart.customer}
          onAttach={() => setCustomerOpen(true)}
          onDetach={() => cart.setCustomer(null)}
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {cart.lines.length === 0 ? (
            <EmptyState
              icon={ShoppingCart}
              title={t('Nothing scanned yet')}
              description={t('Scan a barcode or tap a product to start a sale.')}
            />
          ) : (
            <ul className="divide-y divide-[var(--border-subtle)]">
              {cart.lines.map((line) => (
                <li key={line.productId} className="flex items-center gap-2 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{line.name}</p>
                    <p className="tabular text-xs text-[var(--text-secondary)]">
                      {formatUsd(line.unitPriceUsd)}
                      {line.quantityUnit ? `/${abbreviate(line.unit)}` : ` ${t('each')}`}
                      {line.discountUsd > 0 && ` · −${formatUsd(line.discountUsd)}`}
                    </p>
                  </div>

                  {line.quantityUnit ? (
                    // A weight is not stepped up and down by one; it is re-weighed. The line
                    // shows what was actually put on the scale.
                    <button
                      type="button"
                      onClick={() => {
                        const product = products.data?.find((entry) => entry.id === line.productId)
                        if (product) setWeighing(product)
                      }}
                      className="tabular shrink-0 rounded-lg px-2 py-1 text-sm font-semibold underline-offset-2 hover:underline"
                      aria-label={`Reweigh ${line.name}`}
                    >
                      {trim(line.quantity)} {abbreviate(line.quantityUnit)}
                    </button>
                  ) : (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Less ${line.name}`}
                        onClick={() => cart.setQuantity(line.productId, line.quantity - 1)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="tabular w-10 text-center text-sm font-semibold">
                        {formatQuantity(line.quantity)}
                      </span>
                      {/* Disabled at the last one on the shelf rather than left to fail at
                          checkout, and titled so the cashier is told why it stopped instead of
                          pressing a button that appears dead. */}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`More ${line.name}`}
                        disabled={
                          line.maxQuantity !== undefined && line.quantity >= line.maxQuantity
                        }
                        title={
                          line.maxQuantity !== undefined && line.quantity >= line.maxQuantity
                            ? `Only ${formatQuantity(line.maxQuantity)} on hand`
                            : undefined
                        }
                        onClick={() => cart.setQuantity(line.productId, line.quantity + 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  <span className="tabular w-16 shrink-0 text-right text-sm font-semibold">
                    {formatUsd(lineGross(line))}
                  </span>

                  {canDiscount && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('Discount {name}', { name: line.name })}
                      title={t('Discount this line')}
                      onClick={() => setDiscounting(line)}
                    >
                      <Percent className={line.discountUsd > 0 ? 'h-4 w-4 text-[var(--accent)]' : 'h-4 w-4'} />
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${line.name}`}
                    onClick={() => cart.remove(line.productId)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="safe-bottom border-t border-[var(--border-subtle)] p-4">
          <dl className="mb-3 flex flex-col gap-1 text-sm">
            <div className="flex justify-between text-[var(--text-secondary)]">
              <dt>
                {preview.itemCount === 1
                  ? t('1 item')
                  : t('{count} items', { count: preview.itemCount })}
              </dt>
              <dd className="tabular">{formatUsd(preview.gross)}</dd>
            </div>
            {/* On the whole order, as distinct from the per-line button in the cart. The server
                spreads it across the lines by value before VAT is split, so each line's tax is
                worked out on what it actually sold for. */}
            {canDiscount && (
              <div className="flex justify-between">
                <dt>
                  <button
                    type="button"
                    onClick={() => setOrderDiscountOpen(true)}
                    className="text-[var(--accent)] underline-offset-2 hover:underline"
                  >
                    {cart.saleDiscountUsd > 0 ? t('Order discount') : t('Add order discount')}
                  </button>
                </dt>
                <dd className="tabular">
                  {cart.saleDiscountUsd > 0 ? `−${formatUsd(cart.saleDiscountUsd)}` : ''}
                </dd>
              </div>
            )}
            {/* A shop that does not charge VAT never sees the word. */}
            {settings.data?.vatEnabled !== false && (
              <div className="flex justify-between text-[var(--text-secondary)]">
                <dt>
                  {t('VAT')} {vatInclusive ? t('(included)') : ''}
                </dt>
                <dd className="tabular">{formatUsd(preview.vat)}</dd>
              </div>
            )}
            <div className="flex items-baseline justify-between text-lg font-semibold">
              <dt>{t('Total')}</dt>
              <dd className="tabular">{formatUsd(preview.total)}</dd>
            </div>
            {settings.data?.showLbpOnScreen && lbpRate > 0 && (
              <div className="flex justify-between text-sm text-[var(--text-secondary)]">
                <dt />
                <dd className="tabular">
                  {formatLbp(Math.round((preview.total * lbpRate) / step) * step)}
                </dd>
              </div>
            )}
          </dl>

          <Button
            size="lg"
            full
            disabled={cart.lines.length === 0}
            onClick={() => {
              // A failed attempt's error stays around until cleared explicitly — otherwise a
              // stale "out of stock" for the last item tried still shows once the cart has
              // moved on to something else entirely.
              setError(null)
              setPaymentOpen(true)
            }}
          >
            <Wallet className="h-5 w-5" />
            {t('Pay')} <bdi className="tabular">{formatUsd(preview.total)}</bdi>
          </Button>
        </footer>
      </aside>

      {/* Mobile cart bar */}
      {!isCartOpen && cart.lines.length > 0 && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="safe-bottom fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 bg-[var(--accent)] px-4 py-3 text-[var(--accent-contrast)] lg:hidden"
        >
          <span className="flex items-center gap-2 font-medium">
            <ShoppingCart className="h-5 w-5" />
            <Badge tone="neutral" className="tabular">
              {preview.itemCount}
            </Badge>
          </span>
          <span className="tabular text-lg font-semibold">{formatUsd(preview.total)}</span>
        </button>
      )}

      <PaymentSheet
        open={isPaymentOpen}
        onClose={() => {
          setPaymentOpen(false)
          setError(null)
        }}
        totalUsd={preview.total}
        rate={lbpRate}
        roundingStep={step}
        customer={cart.customer}
        isSubmitting={checkout.isPending}
        error={error}
        onConfirm={(tenders, changeInLbp) => checkout.mutate({ tenders, changeInLbp })}
        onAttachCustomer={() => {
          setPaymentOpen(false)
          setCustomerOpen(true)
        }}
      />

      <CustomItemDialog
        open={isCustomOpen}
        onClose={() => setCustomOpen(false)}
        onAdd={(name, priceUsd, costUsd, quantity) => {
          cart.addCustom(name, priceUsd, costUsd, quantity)
          setFlash(`${name} added`)
          setTimeout(() => setFlash(null), 1200)
        }}
      />

      <DiscountDialog
        open={discounting !== null}
        title={t('Discount — {name}', { name: discounting?.name ?? '' })}
        maximum={discounting ? discounting.unitPriceUsd * (discounting.stockQuantity ?? discounting.quantity) : 0}
        current={discounting?.discountUsd ?? 0}
        onClose={() => setDiscounting(null)}
        onApply={(amount) => discounting && cart.setLineDiscount(discounting.productId, amount)}
      />

      <DiscountDialog
        open={isOrderDiscountOpen}
        title={t('Discount on the whole order')}
        maximum={preview.gross}
        current={cart.saleDiscountUsd}
        onClose={() => setOrderDiscountOpen(false)}
        onApply={(amount) => cart.setSaleDiscount(amount)}
      />

      <WeighDialog
        product={weighing}
        onClose={() => setWeighing(null)}
        onConfirm={(quantity, unit, stockQuantity) => {
          cart.add(weighing!, quantity, unit, stockQuantity)
          setFlash(`${weighing!.name} · ${trim(quantity)} ${abbreviate(unit)}`)
          setTimeout(() => setFlash(null), 1600)
          setWeighing(null)
        }}
      />

      <CustomerPicker
        open={isCustomerOpen}
        onClose={() => setCustomerOpen(false)}
        onPick={(customer) => {
          cart.setCustomer(customer)
          setCustomerOpen(false)
        }}
      />

      <ReceiptDialog saleId={completedSaleId} onClose={() => setCompletedSaleId(null)} />
    </div>
  )
}
